import { useEffect, useState } from "react";
import { Button, message } from "antd";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import CustomInputField from "../../../../../../CustomHTMLElements/CustomInputField";
import CustomTextArea from "../../../../../../../NewComponents/CustomHTMLElements/CustomTextArea";
import useBusinessTypes from "../../../../hooks/useBusinessTypes";
import useUserPositions from "../../../../hooks/useUserPositions";
import useBusinessSectors from "../../../../../../../custom-hooks/useBusinessSectors";
import useWorldCountries from "../../../../../../../custom-hooks/useWorldCountries";
import useCountryStates from "../../../../../../../custom-hooks/useCountryStates";
import { putData } from "../../../../../../../newApis/dotNetApiMethods";
import * as ajaxEndpoints from "../../../../../../../api/ajax-endpoints";
import { AllPartnershipBusinessSteps } from "../..";
import styles from "../../Partnership.module.scss";
import ErrorHandler from "../../../../../../ErrorHandler/ErrorHandler.component";
import { ICorporateCustomer } from "../../../../hooks/useCorporateCustomers";
import { errorHandler } from "../../../../../../../helpers/errorHandler";

const schema = yup.object().shape({
  country: yup
    .string()
    .required("Please select country")
    .min(1, "Country is required")
    .max(128),
  city: yup
    .string()
    .required("Please enter your city name")
    .min(1, "City is required")
    .max(128),
  state: yup
    .string()
    .required("Please enter your state name")
    .min(1, "State is required")
    .max(128),
  businessName: yup
    .string()
    .required("Business name is required")
    .min(1)
    .max(128),
  businessRegistrationNumber: yup.string().required("Please enter rc number"),
  taxIdentificationNumber: yup
    .string()
    .required("Please enter tax identification number"),
  address: yup.string().required("Please enter your address").min(1).max(128),
  businessDescription: yup
    .string()
    .required("Please enter business description"),
  // industryId is not required in funder schema
});

interface IPartnershipBusinessInformation {
  handleProceed: (step: number) => void;
  formData: any;
  resumeCustomerData?: ICorporateCustomer;
}

const PartnershipBusinessInformation: React.FC<
  IPartnershipBusinessInformation
> = ({ handleProceed, formData, resumeCustomerData }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: businessTypesData, isLoading: isLoadingBusinessTypes } =
    useBusinessTypes();
  const { data: userPositionsData, isLoading: isLoadingUserPositions } =
    useUserPositions();
  const { data: businessSectorsData, isLoading: isLoadingBusinessSectors } =
    useBusinessSectors();
  const { data: countriesData, isLoading: isLoadingCountries } =
    useWorldCountries(1); // sourceTypeId 1 for admin/back-office

  // Extract data arrays from response structures
  const businessTypes = businessTypesData?.data || [];
  const userPositions = userPositionsData?.data || [];
  const businessSectors = businessSectorsData?.data || [];
  const countries = countriesData?.data || [];

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      country: "",
      city: "",
      state: "",
      businessName: formData?.businessName || resumeCustomerData?.biz_name,
      businessRegistrationNumber:
        formData?.businessRegistrationNumber ||
        resumeCustomerData?.biz_registration_number,
      taxIdentificationNumber: "",
      address: "",
      businessDescription: "",
      industryId: "",
    },
  });

  const watchCountry = watch("country");

  // Resolve country info to get country ID for state loading (matching funder pattern)
  const resolveCountryInfo = countries?.find(
    ({ iso2 }: { iso2: string }) => iso2 === watchCountry
  );

  const { data: statesData, isLoading: isLoadingStates } = useCountryStates(
    resolveCountryInfo?.id || 0
  );
  const states = statesData?.data || [];

  useEffect(() => {
    if (watchCountry && watchCountry !== "") {
      // Reset state when country changes
      setValue("state", "");
    }
  }, [watchCountry, setValue]);

  const handleSave = async (data: any) => {
    setIsSubmitting(true);
    try {
      const payload = {
        address: data.address,
        businessDescription: data.businessDescription,
        businessName: data.businessName,
        businessRegistrationNumber: data.businessRegistrationNumber,
        businessType: formData?.customerSubType,
        city: data.city,
        industryId: Number(data.industryId),
        state: data.state,
        taxIdentificationNumber: data.taxIdentificationNumber,
        userPosition: formData?.userPosition || resumeCustomerData?.position_id,
        countryCode: data.country,
        customerId: formData?.funder_id || resumeCustomerData?.customer_id,
      };

      const response = await putData(
        ajaxEndpoints.UPDATE_BUSINESS_INFORMATION,
        payload
      );
      message.success(
        response?.data?.message || "Business information saved successfully!"
      );

      handleProceed(AllPartnershipBusinessSteps.BUSINESS_OWNERS_INFORMATION);
    } catch (error: any) {
      message.error(
        errorHandler(error) || "Failed to save business information"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    handleProceed(AllPartnershipBusinessSteps.BUSINESS_OWNERS_INFORMATION);
  };

  const handleSaveClick = () => {
    handleSubmit(handleSave)();
  };

  useEffect(() => {
    setValue(
      "industryId",
      formData?.sector_id || resumeCustomerData?.sector_id
    );
    setValue(
      "businessDescription",
      formData?.biz_description || resumeCustomerData?.biz_description
    );
    setValue("country", "NG");
    setValue("state", formData?.state_name || resumeCustomerData?.state_name);
    setValue("city", formData?.city || resumeCustomerData?.city);
    setValue(
      "address",
      formData?.biz_Address || resumeCustomerData?.biz_Address
    );
  }, []);

  return (
    <form className={styles.businessForm}>
      <div className="row mt-4">
        <div className="col-12">
          <div className="form-group">
            <label>Business Type</label>
            <select
              name="businessType"
              className="form-control"
              value={formData?.customerSubType || ""}
              disabled={true}
            >
              <option value="">Select business type</option>
              {businessTypes.map((type: any) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="col-12 mt-4">
          <CustomInputField
            label="Business Name"
            name="businessName"
            type="text"
            placeholder="Enter business name"
            reference={register}
            errors={errors.businessName}
            readOnly={true}
          />
        </div>

        <div className="col-12 mt-n3">
          <div className="form-group">
            <label>
              Industry {isLoadingBusinessSectors && <small>(Loading...)</small>}
            </label>
            <select name="industryId" className="form-control" ref={register}>
              <option value="">Select industry</option>
              {businessSectors.map((sector: any) => (
                <option key={sector.category_id} value={sector.category_id}>
                  {sector.category_name}
                </option>
              ))}
            </select>
            <ErrorHandler errors={errors.industryId} />
          </div>
        </div>

        <div className="col-12 mt-4">
          <div className="form-group">
            <label>
              Position {isLoadingUserPositions && <small>(Loading...)</small>}
            </label>
            <select
              name="userPosition"
              className="form-control"
              value={formData?.userPosition || resumeCustomerData?.position_id}
              disabled={true}
            >
              <option value="">Select position</option>
              {userPositions.map((position: any) => (
                <option key={position.id} value={position.id}>
                  {position.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="col-12 mt-4">
          <CustomTextArea
            label="How does your business create value?"
            name="businessDescription"
            placeholder="Enter business description"
            reference={register}
            errors={errors.businessDescription}
            showRequiredIcon={true}
          />
        </div>

        <div className="col-12 mt-2">
          <CustomInputField
            label="Tax Identification Number"
            name="taxIdentificationNumber"
            type="text"
            placeholder="Enter tax identification number"
            reference={register}
            errors={errors.taxIdentificationNumber}
          />
        </div>

        <div className="col-12">
          <CustomInputField
            label="Business Registration Number"
            name="businessRegistrationNumber"
            type="text"
            placeholder="Enter business registration number"
            reference={register}
            errors={errors.businessRegistrationNumber}
            readOnly={true}
          />
        </div>

        <div className="col-12 mt-n3">
          <div className="form-group">
            <label>
              Country {isLoadingCountries && <small>(Loading...)</small>}
            </label>
            <select
              name="country"
              className="form-control"
              ref={register}
              onChange={(e) => {
                setValue("country", e.target.value);
              }}
            >
              <option value="">Select country</option>
              {countries.map((country: any) => (
                <option key={country.id} value={country.iso2}>
                  {country.name}
                </option>
              ))}
            </select>
            <ErrorHandler errors={errors.country} />
          </div>
        </div>

        <div className="col-12 mt-4">
          <div className="form-group">
            <label>
              State {isLoadingStates && <small>(Loading...)</small>}
            </label>
            <select name="state" className="form-control" ref={register}>
              <option value="">Select state</option>
              {states.map((state: any) => (
                <option key={state.id} value={state.name}>
                  {state.name}
                </option>
              ))}
            </select>
            <ErrorHandler errors={errors.state} />
          </div>
        </div>

        <div className="col-12 mt-4">
          <CustomInputField
            label="Business City"
            name="city"
            type="text"
            placeholder="Enter city"
            reference={register}
            errors={errors.city}
          />
        </div>

        <div className="col-12">
          <CustomInputField
            label="Business Street Address"
            name="address"
            type="text"
            placeholder="Enter business street address"
            reference={register}
            errors={errors.address}
          />
        </div>
      </div>

      <div className={styles.ActionButtonContainer}>
        <Button
          type="primary"
          size="large"
          onClick={handleSaveClick}
          loading={isSubmitting}
          disabled={isSubmitting}
          className={styles.ActionButton}
        >
          Save
        </Button>
        <Button
          type="default"
          size="large"
          onClick={handleNext}
          className={styles.NextButton}
        >
          Next
        </Button>
      </div>
    </form>
  );
};

export default PartnershipBusinessInformation;
